import includes from 'lodash/includes';
import find from 'lodash/find';
import moment from 'moment';
import {ActionType} from 'redux-promise-middleware';
import uuidV4 from 'uuid/v4';
import FormatUtils from 'utils/format';
import UserUtils from 'user/user-utils';
import SpotUtils from 'utils/spot';
import {
    CHECKOUT_FETCH_RATES,
    CHECKOUT_SHOW_CHANGE_DATE_TIME,
    CHECKOUT_HIDE_CHANGE_DATE_TIME,
    CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS,
    CHECKOUT_UPDATE_DATA,
    CHECKOUT_USER_CHANGE,
    CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS,
    CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS,
    CHECKOUT_PAYMENT_TOKEN_REQUESTED,
    CHECKOUT_PAYMENT_ERROR,
    CHECKOUT_ON_PURCHASE,
    CHECKOUT_PURCHASE_ERROR,
    CHECKOUT_CLEAR_CUSTOMER,
} from './checkout-actions';
import {SPOT_CHANGE_MULTI_RATE, SPOT_SET_TERM} from 'spot/spot-actions';
import {USER_UPDATE, USER_LOGOUT} from 'user/user-actions';
import {ROUTER_DATA_LOAD_FULFILLED} from 'router/router-actions';

let spotData;

const defaultHeroControls = () => ({
    paymentRequired: true,
    paymentRequiredNoCharge: false,
    purchaseForCustomer: false,
    purchaseForCustomerReferralSource: '',
    purchaseForCustomerDescription: 'Unspecified',
    purchaseOnBehalfOfCustomer: false,
    rebookReservationId: null,
    isRebookingReservation: false,
});

const getMonthlyDependentSettings = isMonthly => {
    const supportedFeeTypes =
        spotData.facility && spotData.facility.supported_fee_types
            ? spotData.facility.supported_fee_types
            : null;

    const hasOversizeFee =
        (spotData.facility &&
            spotData.facility.vehicle_size === 'oversized_with_fee' &&
            spotData.facility.oversize_vehicle_type === null) ||
        (supportedFeeTypes && includes(supportedFeeTypes, 'oversize_fee'));

    return {
        licensePlateRequired: isMonthly
            ? true
            : spotData.license_plate_required,
        phoneNumberRequired: isMonthly ? true : spotData.phone_number_required,
        smsParkingPass: false,
        vehicleSelectPresent: isMonthly ? false : hasOversizeFee,
        vehicleSelectRequired: isMonthly ? false : hasOversizeFee,
    };
};

const getCreditCardsForCheckout = (creditCards = []) => {
    const defaultCard = creditCards.length
        ? find(creditCards, {isDefault: true}) || creditCards[0] // if user has cards but no default is set
        : null;

    return {
        selectedCreditCard: defaultCard ? defaultCard.id : 'new',
        isNewCreditCard: !creditCards.length,
    };
};

const getLicensePlatesForCheckout = (licensePlates = []) => {
    const defaultPlate = licensePlates.length
        ? find(licensePlates, {isDefault: true})
        : null;

    return {
        selectedLicensePlate: defaultPlate ? defaultPlate.licensePlate : null,
        newLicensePlate: !defaultPlate,
    };
};

const getVehicleForCheckout = ({vehicleProfileId, vehicles = []}) => {
    const hasVehicles = vehicles && vehicles.length;
    const defaultVehicle =
        vehicleProfileId && (hasVehicles && find(vehicles, {vehicleProfileId}))
            ? {
                  vehicleProfileId,
              }
            : hasVehicles
            ? find(vehicles, {isDefault: true}) || vehicles[0]
            : {
                  vehicleProfileId: null,
                  isUnknown: false,
              };

    return {
        vehicleProfileId: defaultVehicle.vehicleProfileId,
        isVehicleUnknown: defaultVehicle.isUnknown || false,
    };
};

export const getUserSettings = ({
    vehicleProfileId = null,
    userData: {creditCards, licensePlates, vehicles},
}) => ({
    ...(creditCards && getCreditCardsForCheckout(creditCards)),
    ...(licensePlates && getLicensePlatesForCheckout(licensePlates)),
    ...(vehicles &&
        getVehicleForCheckout({
            vehicleProfileId,
            vehicles,
        })),
});

export const initialState = (stateOverrides = null) => ({
    ...defaultHeroControls(),
    // rateSessionId resets every time a rate load is attempted
    rateSessionId: null,
    vehicleSelectPresent: false,
    vehicleSelectRequired: false,
    vehicleProfileId: null,
    vehicleColor: null,
    supportsOversizeFeeType: false,
    isVehicleUnknown: false,
    saveVehicleAsDefault: false,
    selectedLicensePlate: null,
    licensePlateState: null,
    newLicensePlate: true,
    selectedCreditCard: 'new',
    isNewCreditCard: true,
    incomingPromoCode: null,
    promoCode: null,
    adminUser: {},
    currencyType: UserUtils.CURRENCY_TYPES.USD,
    currencySymbol: FormatUtils.currencySymbol(UserUtils.CURRENCY_TYPES.USD),
    affiliate: null,
    licensePlateRequired: false,
    phoneNumberRequired: false,
    contractFullName: null,
    smsParkingPass: false,
    referralSource: '',
    forceRedundantMonthly: false,
    previousRental: false,
    reservationDetailsSkipped: false,
    reservationDetailsEdit: false,
    rid: null,
    isFetchingRates: false,
    highlightAccountPanel: false,
    isPurchasing: false,
    isStaleStart: false,
    showChangeDateTimeModal: false,
    changeDateTimeModalTitle: null,
    changeDateTimeModalIntroMessage: null,
    showWhyTimeExtendedModal: false,
    showRecommendedSpotsModal: false,
    showRedundantMonthlyReservationModal: false,
    redundantMonthlyReservationModalDate: null,
    redundantMonthlyReservationModalParkingPassURL: null,
    recommendedSpotsUnavailableReason: 'Rate unavailable.',
    checkoutError: null,
    checkoutStartTime: moment(),
    changedDateTimeCount: 0,
    isPurchasingWithPaymentRequest: false,
    ...stateOverrides,
});

export default function checkout(
    state = initialState(),
    {type, payload, meta}
) {
    switch (type) {
        case `${CHECKOUT_FETCH_RATES}_${ActionType.Pending}`: {
            const newState = {
                ...state,
                isFetchingRates: true,
                isStaleStart: false,
                showChangeDateTimeModal: false,
                checkoutError: null,
            };

            if (meta.dateTimeChanged) {
                newState.changedDateTimeCount = state.changedDateTimeCount + 1;
            }

            return newState;
        }

        case `${CHECKOUT_FETCH_RATES}_${ActionType.Fulfilled}`: {
            spotData = payload.data.data;
            const {
                facility: {
                    supported_fee_types: supportedFeeTypes, // eslint-disable-line camelcase
                },
                currency_type: currencyType,
            } = spotData;

            const {
                monthly,
                airport,
                rid,
                previousRental,
                starts,
                ends,
                timezone,
            } = meta;
            const newStateData = {
                ...state,
                ...getMonthlyDependentSettings(monthly),
                rateSessionId: uuidV4(),
                currencySymbol: FormatUtils.currencySymbol(currencyType),
                currencyType: currencyType.toUpperCase(),
                isFetchingRates: false,
                checkoutError: null,
                recommendedSpotsUnavailableReason: 'Rate unavailable.',
                previousRental: previousRental || false,
                vehicleProfileId:
                    state.spot && state.spot.spotId !== spotData.parking_spot_id
                        ? null
                        : state.vehicleProfileId,
                rid,
                spot: SpotUtils.prepare({
                    spot: spotData,
                    searchRequest: {
                        monthly,
                        airport,
                        starts,
                        ends,
                    },
                    timezone,
                }),
                supportsOversizeFeeType:
                    supportedFeeTypes &&
                    includes(supportedFeeTypes, 'oversize_fee'),
            };

            if (!newStateData.spot.selectedRate) {
                const unavailableRate = monthly
                    ? newStateData.spot.monthly_rates[0]
                    : newStateData.spot.hourly_rates[0];
                const reasonType = monthly ? 'Monthly' : 'Transient';

                if (unavailableRate && unavailableRate.unavailable) {
                    newStateData.recommendedSpotsUnavailableReason = `${reasonType} Unavailable Reason: ${unavailableRate.unavailable_reason}`;
                }

                if (!previousRental && !newStateData.isStaleStart) {
                    newStateData.showChangeDateTimeModal = true;
                    newStateData.changeDateTimeModalIntroMessage =
                        'Select your reservation start and end so we can lock in your low rate – up to 50% off street parking.';
                }
            }

            return newStateData;
        }

        case `${CHECKOUT_FETCH_RATES}_${ActionType.Rejected}`: {
            return {
                ...state,
                rateSessionId: uuidV4(),
                isFetchingRates: false,
                checkoutError: {
                    subtitle: `Correct the following mistakes then press Pay & Reserve again. Don't worry, you won't be charged twice.`,
                    errors: payload.data.data.errors[0].messages,
                },
            };
        }

        case CHECKOUT_SHOW_CHANGE_DATE_TIME: {
            return {
                ...state,
                showChangeDateTimeModal: true,
                isStaleStart: payload.isStaleStart,
                changeDateTimeModalTitle: payload.title,
                changeDateTimeModalIntroMessage: payload.message,
            };
        }

        case CHECKOUT_HIDE_CHANGE_DATE_TIME: {
            return {
                ...state,
                showChangeDateTimeModal: false,
                showRecommendedSpotsModal: payload.showRecommendedSpots,
            };
        }

        case CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS: {
            return {
                ...state,
                showRecommendedSpotsModal: false,
                showChangeDateTimeModal: payload.showChangeDateTimeModal,
                changeDateTimeModalIntroMessage:
                    'Just FYI, the price or availability might change if you choose different options here.',
            };
        }

        case `${CHECKOUT_UPDATE_DATA}_FULFILLED`:
        case CHECKOUT_UPDATE_DATA: {
            return {
                ...state,
                ...payload,
            };
        }

        case CHECKOUT_USER_CHANGE: {
            const {user, userNormalized, extraReservationItems} = payload;

            if (!user || user.status === UserUtils.AUTH_STATE.LOGGED_OUT) {
                return {
                    ...state,
                    selectedCreditCard: 'new',
                    isNewCreditCard: true,
                    selectedLicensePlate: null,
                    licensePlateState: null,
                    newLicensePlate: true,
                    paymentRequired: true,
                    paymentRequiredNoCharge: false,
                    promoCode: null,
                    vehicleProfileId: null,
                    vehicleColor: null,
                    isVehicleUnknown: false,
                    smsParkingPass: false,
                    reservationDetailsSkipped: false,
                    reservationDetailsEdit: false,
                    ...extraReservationItems,
                };
            } else if (user.status === UserUtils.AUTH_STATE.USER) {
                const userData = !userNormalized
                    ? UserUtils.normalize(user)
                    : user;
                const {vehicleProfileId} = state;

                return {
                    ...state,
                    ...getUserSettings({
                        vehicleProfileId,
                        userData,
                    }),
                    unknownLicensePlate: false,
                    reservationDetailsSkipped: false,
                    reservationDetailsEdit: false,
                    smsParkingPass: false,
                    ...extraReservationItems,
                };
            } else if (user.status === UserUtils.AUTH_STATE.GUEST) {
                return {
                    ...state,
                    smsParkingPass: user.smsParkingPass,
                    reservationDetailsSkipped: false,
                    reservationDetailsEdit: false,
                    vehicleProfileId: null,
                    vehicleColor: null,
                    isVehicleUnknown: false,
                    selectedLicensePlate: null,
                    licensePlateState: null,
                    ...extraReservationItems,
                };
            }

            return {
                ...state,
                ...extraReservationItems,
            };
        }

        case USER_UPDATE: {
            const {vehicleProfileId} = state;

            return {
                ...state,
                ...getUserSettings({
                    vehicleProfileId,
                    userData: payload,
                }),
            };
        }

        case CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS: {
            return {
                ...state,
                showWhyTimeExtendedModal: !state.showWhyTimeExtendedModal,
            };
        }

        case CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS: {
            return {
                ...state,
                showRedundantMonthlyReservationModal: false,
            };
        }

        case CHECKOUT_PAYMENT_TOKEN_REQUESTED: {
            return {
                ...state,
                isPurchasing: true,
                checkoutError: null,
            };
        }

        case CHECKOUT_PAYMENT_ERROR: {
            return {
                ...state,
                isPurchasing: false,
                checkoutError: {
                    subtitle: `Correct the following mistakes then press Pay & Reserve again. Don't worry, you won't be charged twice.`,
                    errors: payload.errors,
                },
            };
        }

        case `${CHECKOUT_ON_PURCHASE}_${ActionType.Pending}`: {
            const isPurchasingWithPaymentRequest = meta
                ? meta.isPurchasingWithPaymentRequest
                : false;

            return {
                ...state,
                isPurchasing: true,
                redundantMonthlyReservationModalDate: null,
                redundantMonthlyReservationModalParkingPassURL: null,
                showRedundantMonthlyReservationModal: false,
                checkoutError: null,
                isPurchasingWithPaymentRequest,
            };
        }

        case `${CHECKOUT_ON_PURCHASE}_${ActionType.Rejected}`: {
            return {
                ...state,
                isPurchasing: false,
                checkoutError: null,
            };
        }

        case CHECKOUT_PURCHASE_ERROR: {
            return {
                ...state,
                isPurchasing: false,
                forceRedundantMonthly: false,
                ...payload,
            };
        }

        case SPOT_CHANGE_MULTI_RATE: {
            return {
                ...state,
                rid: payload.rid,
            };
        }

        case SPOT_SET_TERM: {
            const {monthly, rid} = payload;

            return {
                ...state,
                ...getMonthlyDependentSettings(monthly),
                rid,
            };
        }

        case `${USER_LOGOUT}_${ActionType.Fulfilled}`:
        case CHECKOUT_CLEAR_CUSTOMER: {
            return {
                ...state,
                ...defaultHeroControls(),
                promoCode: null,
                adminUser: initialState().adminUser,
            };
        }

        case ROUTER_DATA_LOAD_FULFILLED: {
            const {
                checkout: {rid},
            } = payload;

            return {
                ...state,
                rid,
            };
        }

        default:
            return state;
    }
}
